import { Box, Button, FormControl, FormHelperText, IconButton, Typography } from '@mui/joy';
import { Textarea } from 'components/styled/TextArea';
import { InterviewStageEditProps } from './InterviewMetaInfoEdit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { grey } from '@mui/material/colors';
import { string, array, object } from 'yup';
import { useState } from 'react';
import { useListErrors } from './useListErrors';

const criteriaSchema = string()
  .min(3, 'Criteria must be at least 3 characters')
  .required('Required');

const criteriaListSchema = array(criteriaSchema).min(1, 'Enter at least one screening criteria');

export const screeningCriteriaSchema = object({
  criteria: criteriaListSchema,
});

export function InterviewScreeningCriteriaEdit({
  interviewConfig,
  handleChange,
  handleBack,
  handleNext,
  submitting,
}: InterviewStageEditProps) {
  const { itemErrors, validateItem, validateList, listErrors } = useListErrors(
    criteriaSchema,
    criteriaListSchema
  );

  const onCriteriaChange = (index: number, value: string) => {
    const newCriterias = [...(interviewConfig?.screeningCriteria?.criteria || [])];
    newCriterias[index] = value;
    handleChange({ ...interviewConfig, screeningCriteria: { criteria: newCriterias } });
    validateItem(index, value);
  };

  const onCriteriaRemove = (index: number) => {
    const newCriterias = [...(interviewConfig?.screeningCriteria?.criteria || [])];
    newCriterias.splice(index, 1);
    handleChange({ ...interviewConfig, screeningCriteria: { criteria: newCriterias } });
    validateList(newCriterias);
  };

  const onCriteriaCreate = () => {
    const newCriterias = [...(interviewConfig?.screeningCriteria?.criteria || []), ''];
    handleChange({ ...interviewConfig, screeningCriteria: { criteria: newCriterias } });
    validateList(newCriterias);
  };

  const validate = () => {
    return validateList(interviewConfig?.screeningCriteria?.criteria || []);
  };

  const handleSubmit = () => {
    if (validate()) {
      handleNext();
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: 'calc(100vh - 260px)',
          overflowY: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{ height: '100%', overflowY: 'auto', padding: '20px', boxSizing: 'border-box' }}
        >
          <Typography level="title-md" gutterBottom sx={{ marginBottom: '10px' }}>
            Add criteria you want to assess the candidate on.
          </Typography>
          <Typography level="body-sm" gutterBottom sx={{ marginBottom: '20px' }}>
            These criteria will be used by the AI to score the candidate. The assessment of the test
            will be based on the criteria you add here.
          </Typography>
          <FormControl error={!!listErrors}>
            {interviewConfig?.screeningCriteria?.criteria?.map((criteria, index) => (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: '20px', width: 600 }}
              >
                <FormControl error={!!itemErrors[index]} sx={{ width: 400 }}>
                  <Typography level="title-sm" gutterBottom>
                    Criteria {index + 1}
                  </Typography>
                  <Textarea
                    value={criteria}
                    onChange={e => onCriteriaChange(index, e.target.value)}
                    minRows={3}
                    size={'sm'}
                  />
                  {itemErrors[index] && <FormHelperText>{itemErrors[index]}</FormHelperText>}
                </FormControl>
                <IconButton variant="plain" onClick={() => onCriteriaRemove(index)}>
                  <DeleteOutlineIcon style={{ color: grey[600] }} />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => onCriteriaCreate()}
              sx={{ marginBottom: '20px' }}
            >
              Add criteria
            </Button>
            {listErrors && <FormHelperText>{listErrors}</FormHelperText>}
          </FormControl>
        </div>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Button variant="plain" color="neutral" onClick={() => handleBack()}>
          Back
        </Button>
        <Button onClick={() => handleSubmit()} loading={submitting}>
          Next
        </Button>
      </Box>
    </>
  );
}
